// CRM/JourneyTimeline.jsx — shared vertical "user journey" timeline.
//
// Renders a contact's CrmJourneyEvent feed (GET /api/crm/contacts/:id/timeline)
// newest-first: each step is a dot + title + optional state badge + relative +
// absolute timestamp + proof, with an upward arrow connecting to the next
// (more recent) step. Used by the CRM kanban contact drawer and the Email
// Management contact panel.

import React, { useEffect, useState } from 'react';
import { get } from '../../api';

// Relative time, e.g. "3 weeks ago" / "just now".
export function relTime(d) {
  if (!d) return '';
  const t = new Date(d).getTime();
  if (isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
  for (const [name, secs] of units) {
    const n = Math.floor(s / secs);
    if (n >= 1) return `${n} ${name}${n > 1 ? 's' : ''} ago`;
  }
  return 'just now';
}

function fmtAbs(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleString();
}

const JTYPE = {
  status_change:  { icon: '⇄', color: '#2563eb' },
  login:          { icon: '→', color: '#16a34a' },
  signup:         { icon: '★', color: '#16a34a' },
  demo:           { icon: '▶', color: '#7c3aed' },
  purchase:       { icon: '$', color: '#15803d' },
  license:        { icon: '✓', color: '#15803d' },
  email_sentiment:{ icon: '✉', color: '#b45309' },
  chat_sentiment: { icon: '💬', color: '#b45309' },
  support:        { icon: '?', color: '#dc2626' },
  activity:       { icon: '•', color: '#64748b' },
  sync:           { icon: '↻', color: '#64748b' },
};
const SENT_COLOR = { positive: '#16a34a', negative: '#dc2626', neutral: '#64748b' };

// Accepts either a contactId (CRM/email panels) or an email (chat inbox, where
// only the participant's email is known — resolves to the contact server-side).
export default function JourneyTimeline({ contactId, email, limit = 200 }) {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    const url = contactId
      ? `/api/crm/contacts/${contactId}/timeline?limit=${limit}`
      : `/api/crm/contacts/by-email/${encodeURIComponent(email)}/timeline?limit=${limit}`;
    if (!contactId && !email) { setEvents([]); return undefined; }
    get(url)
      .then((r) => { if (alive) setEvents((r && r.data) || []); })
      .catch((e) => { if (alive) setErr((e && e.message) || 'Timeline failed'); });
    return () => { alive = false; };
  }, [contactId, email, limit]);

  if (err) return <div style={{ fontSize: 12, color: '#a71d2a' }}>{err}</div>;
  if (!events) return <div style={{ fontSize: 12, color: '#888' }}>Loading timeline…</div>;
  if (events.length === 0) return <div style={{ fontSize: 12, color: '#888' }}>No journey events yet. They appear after the next CRM sync / analysis.</div>;

  return (
    <div style={{ position: 'relative', paddingLeft: 6 }}>
      {events.map((e, i) => {
        const t = JTYPE[e.type] || JTYPE.activity;
        const dot = e.sentiment ? (SENT_COLOR[e.sentiment] || t.color) : t.color;
        const last = i === events.length - 1;
        return (
          <div key={e._id} style={{ position: 'relative', paddingLeft: 26, paddingBottom: last ? 0 : 16 }}>
            {!last && <div style={{ position: 'absolute', left: 9, top: 20, bottom: -2, width: 2, background: '#e5e7eb' }} />}
            {i > 0 && <div style={{ position: 'absolute', left: 4, top: -14, fontSize: 12, color: '#cbd5e1' }}>↑</div>}
            <div style={{ position: 'absolute', left: 0, top: 1, width: 20, height: 20, borderRadius: 10, background: dot, color: '#fff', fontSize: 11, lineHeight: '20px', textAlign: 'center' }}>{t.icon}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{e.title || e.type}</span>
              {e.state && <span style={{ background: '#eef2ff', color: '#3730a3', padding: '1px 7px', borderRadius: 10, fontSize: 10 }}>{e.state}</span>}
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{relTime(e.at)}</span>
            </div>
            {e.proof && <div style={{ fontSize: 12, color: '#475569', marginTop: 2, whiteSpace: 'pre-wrap' }}>{e.proof}</div>}
            <div style={{ fontSize: 10, color: '#b6bcc6', marginTop: 2 }}>{fmtAbs(e.at)}{e.source ? ` · ${e.source}` : ''}</div>
          </div>
        );
      })}
    </div>
  );
}
